import { useMachine } from "@xstate/react";
import qs from "qs";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  AsyncTypeaheadSearch,
  FilterName,
  KeyValue,
  MetaSegment,
  WorldLocation,
} from "@skv/core";

import {
  organisationResultStore,
  OrganisationSearchQueryState,
  searchFormMachine,
  usePageEnvelope,
  useThunkDispatch,
} from "@skv/state";

import { LocationMenu } from "@skv/core/src/features/search";

interface Organisation {
  category: MetaSegment<number>;
  friendlyName: string;
  id: number;
  location: WorldLocation;
  name: string;
  url: string;
}

interface OrganisationSearchPageEnvelopeData {
  organisationFilters: {
    categoryList: KeyValue[];
  };
  organisationResult: {
    canonicalUrl: string;
    organisations: Organisation[];
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  searchCriteria: {
    keyword: string;
    category: number[];
    where: string | null;
    page: number | null;
  };
}

export const OrganisationSearchForm = () => {
  const history = useHistory();
  const location = useLocation<{
    searchQuery?: OrganisationSearchQueryState;
  }>();
  const dispatch = useThunkDispatch();

  const { pageEnvelope } =
    usePageEnvelope<OrganisationSearchPageEnvelopeData>();

  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const initialSearchQuery = React.useMemo(() => {
    return Object.assign(
      {},
      pageEnvelope.data?.searchCriteria,
      location.state?.searchQuery
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [current, send] = useMachine(searchFormMachine, {
    context: {
      keyword: initialSearchQuery.keyword || "",
      category: initialSearchQuery.category || [],
      where: initialSearchQuery.where || "",
    },
  });

  const [openFilter, setOpenFilter] = React.useState<FilterName | null>(null);

  const [keyword, setKeyword] = React.useState(
    initialSearchQuery.keyword || (query.keyword as string) || ""
  );

  const [whereSelected] = React.useState(
    !!pageEnvelope.data?.searchCriteria?.where
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const searchQuery = {
      ...location.state?.searchQuery,
      keyword,
      where: current.context.where,
    };

    dispatch(
      organisationResultStore.actions.fetchOrganisations(searchQuery)
    ).then((data) => {
      history.push(data.canonicalUrl, {
        searchQuery,
        analytics: {
          didWhereAutoComplete: whereSelected,
        },
      });
    });
  };

  return (
    <section>
      <div className="nsw-container mt-n5">
        <div className="bg-secondary p-4">
          <form
            css={`
              .form-control {
                border-color: #fff;
              }
            `}
            method="GET"
            onSubmit={handleSubmit}
            action="/volunteering-organisations"
          >
            <div className="row align-items-end mx-n2 mt-lg-n3">
              <div
                className="col-md nsw-form__group d-flex flex-column px-2"
                css={`
                  overflow-x: clip;
                `}
              >
                <label className="nsw-form__label" htmlFor="keyword">
                  Keyword
                </label>
                <span className="sr-only">Name or keyword</span>
                <input
                  className="nsw-form__input focus-primary border-primary"
                  id="keyword"
                  name="keyword"
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="Name or keyword"
                />
              </div>
              <div
                className="col-md nsw-form__group d-flex flex-column px-2"
                css={`
                  overflow-x: clip;
                `}
              >
                <label className="nsw-form__label" htmlFor="where">
                  Location
                </label>
                <span className="sr-only">Enter suburb, town or city</span>
                <AsyncTypeaheadSearch<string>
                  id="where"
                  inputProps={{
                    id: "where",
                    name: "where",
                    autoComplete: "off",
                    className: "nsw-form__input focus-primary border-primary",
                  }}
                  wrapperProps={{
                    className: "nsw-form__label",
                  }}
                  open={openFilter === "where"}
                  onFocus={() => setOpenFilter("where")}
                  onBlur={() => setOpenFilter(null)}
                  onInputChange={(text) => {
                    send({ type: "LOCATION_INPUT_CHANGE", value: text });
                    if (openFilter !== "where") {
                      setOpenFilter("where");
                    }
                  }}
                  onSearch={(text) => {
                    if (
                      current.context.searchLocationsFullList.length === 0 &&
                      text.length > 2
                    ) {
                      send({
                        type: "FETCH_START_LOCATIONS",
                        value: text,
                      });
                    }
                  }}
                  onChange={(selected) => {
                    if (selected.length) {
                      Promise.resolve()
                        .then(() =>
                          send({
                            type: "LOCATION_INPUT_SELECT",
                            value: selected[0],
                          })
                        )
                        .then(() => setOpenFilter(null));
                    }
                  }}
                  onClear={() => {
                    Promise.resolve().then(() => {
                      send({ type: "LOCATION_INPUT_CLEAR" });
                      setOpenFilter(null);
                    });
                  }}
                  isLoading={
                    current.matches("searchLocationsFullList.loading") &&
                    current.matches("relatedSuggestions.loading")
                  }
                  options={(() => {
                    if (current.context.where.length <= 2) {
                      return current.context.initialSuggestions;
                    }

                    const query = current.context.where.toLowerCase();
                    if (current.context.searchLocationsFullList.length) {
                      return current.context.searchLocationsFullList.filter(
                        (n) => n.toLowerCase().includes(query)
                      );
                    }

                    return current.context.relatedSuggestions || [];
                  })()}
                  defaultInputValue={current.context.where}
                  selected={
                    current.context.where ? [current.context.where] : []
                  }
                  minLength={0}
                  renderMenu={(results, menuProps, menuComponents) => (
                    <LocationMenu
                      results={results}
                      menuProps={menuProps}
                      menuComponents={menuComponents}
                      search={current.context.where}
                      isLoading={
                        current.matches("initialSuggestions.loading") ||
                        (current.matches("searchLocationsFullList.loading") &&
                          current.matches("relatedSuggestions.loading"))
                      }
                    />
                  )}
                />
              </div>
              <div className="px-2 nsw-form__group flex-grow-1 flex-md-grow-0">
                <button
                  className="btn btn-block btn-primary"
                  aria-label="Search for opportunities"
                  css={`
                    min-width: 100px;
                  `}
                >
                  Find
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
